import { Award, Building2, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

function Certifications() {
  const activeCertifications = [
    { name: 'SAM Certified', icon: <Award className="w-16 h-16" />, description: 'System for Award Management registered for federal contracting opportunities', category: 'Government' },
    { name: 'FEMA Certified', icon: <Building2 className="w-16 h-16" />, description: 'Federal Emergency Management Agency certified contractor for disaster response and recovery operations', category: 'Government' },
    { name: 'SBA Registered', icon: <Building2 className="w-16 h-16" />, description: 'Small Business Administration registered contractor', category: 'Government' },
    { name: 'Fed Links Certified', icon: <Award className="w-16 h-16" />, description: 'Federal procurement network certified member', category: 'Government' },
    { name: 'OSHA 30 Certified', icon: <Award className="w-16 h-16" />, description: 'OSHA 30-Hour Construction Safety certification for comprehensive workplace safety compliance', category: 'Safety' },
    { name: 'Licensed & Bonded', icon: <Award className="w-16 h-16" />, description: 'Fully licensed, bonded, and comprehensively insured for all commercial and government projects', category: 'Compliance' },
    { name: 'EPA Lead-Safe Certified', icon: <Award className="w-16 h-16" />, description: 'Environmental Protection Agency certified for safe lead paint renovation and abatement work', category: 'Safety' },
    { name: 'GSA Schedule Contractor', icon: <Building2 className="w-16 h-16" />, description: 'General Services Administration approved contractor for streamlined federal procurement', category: 'Government' },
    { name: 'CMMC Level 1', icon: <Shield className="w-16 h-16" />, description: 'Cybersecurity Maturity Model Certification (CMMC) Level 1 compliant for safeguarding federal contract information.', category: 'Compliance' },
    { name: 'NIST SP 800-171', icon: <Shield className="w-16 h-16" />, description: 'NIST SP 800-171 cybersecurity self-assessment with an SPRS score of 84, demonstrating implemented controls for protecting Controlled Unclassified Information (CUI).', category: 'Compliance' }
  ];

  const inProgressCertifications = [
    { name: 'HUBZone Certification', icon: <Building2 className="w-16 h-16" />, description: 'Historically Underutilized Business Zone program application in progress', status: 'In Progress' },
    { name: 'ISO Certification', icon: <Award className="w-16 h-16" />, description: 'International Organization for Standardization quality management certification in progress', status: 'In Progress' }
  ];

  return (
    <div className="pt-28">
      {/* Hero Section */}
      <section className="bg-black py-24 relative overflow-hidden">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="absolute inset-0 shimmer-wave"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <span className="text-gold text-sm font-semibold tracking-[0.3em] uppercase mb-4 block">Credentials & Certifications</span>
          <div className="w-24 h-1 bg-gold mx-auto mb-8"></div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Licensed, Certified & <span className="text-gold">Trusted</span>
          </h1>
          <p className="text-gray-300 text-xl max-w-3xl mx-auto">
            Our certifications demonstrate our commitment to safety, quality, and professional excellence
          </p>
        </div>
      </section>

      {/* Active Certifications */}
      <section className="bg-black py-32 relative overflow-hidden border-t border-gold/30" data-testid="certifications-section">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
              Active <span className="text-gold">Certifications</span>
            </h2>
            <div className="w-24 h-1 bg-gold mx-auto mb-8"></div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {activeCertifications.map((cert, index) => (
              <div 
                key={index}
                className="relative overflow-hidden rounded-lg border-2 border-gold/30 hover:border-gold transition-all group hover:transform hover:scale-105 hover:shadow-2xl min-h-[280px] bg-gradient-to-br from-black to-gray-900"
                data-testid={`certification-${index}`}
              >
                <div className="p-10">
                  <div className="text-gold mb-6 group-hover:scale-110 transition-transform">{cert.icon}</div>
                  <div className="mb-3">
                    <span className="text-gold/70 text-xs uppercase tracking-wider font-semibold">{cert.category}</span>
                  </div>
                  <h4 className="text-white font-bold text-2xl mb-4 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                    {cert.name}
                  </h4>
                  <p className="text-gray-300 leading-relaxed text-sm">{cert.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications In Progress */}
      <section className="bg-black py-32 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
              Certifications <span className="text-gold">In Progress</span>
            </h2>
            <div className="w-24 h-1 bg-gold mx-auto mb-8"></div>
            <p className="text-gray-300 text-xl max-w-3xl mx-auto">
              We are actively pursuing additional certifications to better serve our clients
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-10 max-w-5xl mx-auto">
            {inProgressCertifications.map((cert, index) => (
              <div 
                key={index}
                className="relative overflow-hidden rounded-lg border-2 border-gold/20 hover:border-gold/50 transition-all group min-h-[250px] bg-gradient-to-br from-black to-gray-900/50"
              >
                <div className="absolute top-4 right-4">
                  <span className="bg-gold/20 text-gold px-4 py-1 rounded-full text-xs font-semibold uppercase tracking-wider">
                    {cert.status}
                  </span>
                </div>
                <div className="p-10">
                  <div className="text-gold/60 mb-6 group-hover:scale-110 transition-transform">{cert.icon}</div>
                  <h4 className="text-white font-bold text-2xl mb-4 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                    {cert.name}
                  </h4>
                  <p className="text-gray-400 leading-relaxed text-sm">{cert.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Info Section */}
      <section className="bg-black py-32 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-black border border-gold/30 rounded-lg p-12">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-6 text-center font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
              Commitment to <span className="text-gold">Excellence</span>
            </h3>
            <p className="text-gray-300 text-lg leading-relaxed text-center mb-8">
              Our certifications and licenses represent more than compliance—they reflect our unwavering dedication to delivering safe, sustainable, and exceptional construction services. We continuously invest in training, technology, and best practices to maintain the highest industry standards.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h3 className="text-4xl md:text-5xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Work With <span className="text-gold">Certified Professionals</span>
          </h3>
          <p className="text-gray-300 text-xl mb-10">
            Partner with a team that prioritizes safety, quality, and compliance
          </p>
          <Link 
            to="/contact"
            className="inline-block bg-gold hover:bg-gold-dark text-black font-bold py-5 px-12 rounded text-lg transition-all transform hover:scale-105 uppercase tracking-wider"
          >
            Get In Touch
          </Link>
        </div>
      </section>
    </div>
  );
}

export default Certifications;